import { API_URL, printLog } from '../constants/constants.ts';
import { normalizeChapterLike, normalizeTimingFields } from '../utils/normalizeTimings.ts';

export interface ChapterMetadata {
  headline: string;
  summary?: string;
  keywords: string[];
  startTime: number;
  endTime: number;
  duration: number;
  chapterNumber: number;
  [key: string]: any;
}

export interface Chapter {
  id: string;
  metadata: ChapterMetadata;
  chapterNumber: number;
  headline: string;
  startTime: number;
  endTime: number;
}

export interface EpisodeMetadata {
  title: string;
  description?: string;
  audioUrl?: string;
  duration?: number;
  feedId?: string;
  imageUrl?: string;
  [key: string]: any;
}

export interface Episode {
  id: string;
  guid: string;
  metadata: EpisodeMetadata;
}

export interface EpisodeWithChaptersResponse {
  success: boolean;
  guid: string;
  episode: Episode;
  chapters: Chapter[];
  chapterCount: number;
}

interface CachedEpisodeChapters {
  episodeId: string;
  lastUpdated: number;
  chapters: Chapter[];
  episode: Episode;
  chapterCount: number;
}

const CACHE_KEY_PREFIX = 'episode_chapters_';
const CACHE_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours
const MAX_CACHED_EPISODES = 15;

class ChapterService {
  private getCacheKey(episodeId: string): string {
    return `${CACHE_KEY_PREFIX}${episodeId}`;
  }

  private getAllCacheEntries(): { key: string; lastUpdated: number }[] {
    const entries: { key: string; lastUpdated: number }[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key?.startsWith(CACHE_KEY_PREFIX)) continue;
      try {
        const raw = localStorage.getItem(key);
        if (raw) {
          const parsed = JSON.parse(raw);
          entries.push({ key, lastUpdated: parsed.lastUpdated ?? 0 });
        }
      } catch {
        localStorage.removeItem(key!);
      }
    }
    return entries;
  }

  private purgeExpiredEntries(): void {
    const now = Date.now();
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (!key?.startsWith(CACHE_KEY_PREFIX)) continue;
      try {
        const raw = localStorage.getItem(key);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (now - (parsed.lastUpdated ?? 0) > CACHE_EXPIRY_MS) {
            localStorage.removeItem(key);
          }
        }
      } catch {
        localStorage.removeItem(key!);
      }
    }
  }

  private evictOldest(keepRoom: number = 1): void {
    const entries = this.getAllCacheEntries();
    entries.sort((a, b) => a.lastUpdated - b.lastUpdated);
    const toRemove = entries.length - (MAX_CACHED_EPISODES - keepRoom);
    for (let i = 0; i < toRemove && i < entries.length; i++) {
      localStorage.removeItem(entries[i].key);
    }
  }

  private getCachedData(episodeId: string): CachedEpisodeChapters | null {
    try {
      const cacheKey = this.getCacheKey(episodeId);
      const cached = localStorage.getItem(cacheKey);
      
      if (!cached) {
        return null;
      }

      const data: CachedEpisodeChapters = JSON.parse(cached);
      
      const now = Date.now();
      if (now - data.lastUpdated > CACHE_EXPIRY_MS) {
        printLog(`Cache expired for episode ${episodeId}`);
        localStorage.removeItem(cacheKey);
        return null;
      }

      printLog(`Cache hit for episode ${episodeId}`);
      return data;
    } catch (error) {
      console.error('Error reading from cache:', error);
      return null;
    }
  }

  private setCachedData(episodeId: string, response: EpisodeWithChaptersResponse): void {
    const cacheKey = this.getCacheKey(episodeId);
    const data: CachedEpisodeChapters = {
      episodeId,
      lastUpdated: Date.now(),
      chapters: response.chapters,
      episode: response.episode,
      chapterCount: response.chapterCount
    };

    const payload = JSON.stringify(data);

    // Purge expired first, then enforce the cap
    this.purgeExpiredEntries();
    this.evictOldest();

    try {
      localStorage.setItem(cacheKey, payload);
      printLog(`Cached data for episode ${episodeId}`);
    } catch {
      // Quota still exceeded — shed more entries and retry
      this.evictOldest(Math.ceil(MAX_CACHED_EPISODES / 2));
      try {
        localStorage.setItem(cacheKey, payload);
      } catch {
        printLog(`Failed to cache episode ${episodeId} — localStorage full`);
      }
    }
  }

  async fetchEpisodeWithChapters(episodeGuid: string): Promise<EpisodeWithChaptersResponse> {
    printLog(`ChapterService: Fetching episode with chapters for ${episodeGuid}`);

    // Check cache first
    const cached = this.getCachedData(episodeGuid);
    if (cached) {
      const cachedResponse: EpisodeWithChaptersResponse = {
        success: true,
        guid: episodeGuid,
        episode: cached.episode,
        chapters: cached.chapters,
        chapterCount: cached.chapterCount
      };
      // Normalize cached data too (older cache entries may be snake_case).
      return {
        ...cachedResponse,
        episode: {
          ...cachedResponse.episode,
          metadata: normalizeTimingFields(cachedResponse.episode.metadata as any),
        },
        chapters: cachedResponse.chapters.map(ch => normalizeChapterLike(ch as any)),
      };
    }

    // Fetch from API
    try {
      const response = await fetch(`${API_URL}/api/episode-with-chapters/${episodeGuid}`);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Failed to fetch episode chapters: ${response.status} - ${errorData.error || response.statusText}`);
      }

      const data: EpisodeWithChaptersResponse = await response.json();
      printLog(`ChapterService: Fetched ${data.chapterCount} chapters for ${episodeGuid}`);

      // Normalize timing fields so consumers can rely on startTime/endTime regardless of casing.
      const normalized: EpisodeWithChaptersResponse = {
        ...data,
        episode: data.episode
          ? { ...data.episode, metadata: normalizeTimingFields((data.episode as any).metadata) }
          : data.episode,
        chapters: Array.isArray(data.chapters) ? data.chapters.map(ch => normalizeChapterLike(ch as any)) : data.chapters,
      };

      // Cache the normalized result
      this.setCachedData(episodeGuid, normalized);

      return normalized;
    } catch (error) {
      printLog(`ChapterService: Error fetching episode chapters for ${episodeGuid}: ${error}`);
      throw error;
    }
  }

  clearCache(episodeId?: string): void {
    if (episodeId) {
      localStorage.removeItem(this.getCacheKey(episodeId));
      printLog(`Cleared cache for episode ${episodeId}`);
    } else {
      // Clear all episode chapter caches
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(CACHE_KEY_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
      printLog('Cleared all episode chapter caches');
    }
  }
}

export default new ChapterService();

